import type { ComponentBaseProps, EventSection } from "@/ui/componentBase";
import type React from "react";
import { getBackgroundColorClass } from "@/lib/colors";
import { getState, getStateMap, getTriggerRenderer } from "..";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  ExecutionInfo,
  NodeInfo,
  OutputPayload,
  SubtitleContext,
} from "../types";
import type { MetadataItem } from "@/ui/metadataList";
import cloudsmithIcon from "@/assets/icons/integrations/cloudsmith.svg";
import { renderTimeAgo } from "@/components/TimeAgo";
import type {
  VulnerabilityPolicyConfiguration,
  VulnerabilityPolicyData,
  VulnerabilityPolicyNodeMetadata,
} from "./types";

export const getVulnerabilityPolicyMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;
    const componentName = context.componentDefinition.name ?? "cloudsmith";

    return {
      iconSrc: cloudsmithIcon,
      collapsedBackground: getBackgroundColorClass(context.componentDefinition.color),
      collapsed: context.node.isCollapsed,
      title: context.node.name || context.componentDefinition.label || "Unnamed component",
      eventSections: lastExecution ? buildEventSections(context.nodes, lastExecution, componentName) : undefined,
      metadata: buildMetadata(context.node),
      includeEmptyState: !lastExecution,
      eventStateMap: getStateMap(componentName),
    };
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, unknown> {
    const details: Record<string, string> = {};

    if (context.execution.createdAt) {
      details["Executed At"] = new Date(context.execution.createdAt).toLocaleString();
    }

    const outputs = context.execution.outputs as { default?: OutputPayload[] } | undefined;
    const policy = outputs?.default?.[0]?.data as VulnerabilityPolicyData | undefined;
    if (!policy) return details;

    addPolicyDetails(details, policy);

    return details;
  },

  subtitle(context: SubtitleContext): string | React.ReactNode {
    if (!context.execution.createdAt) return "";
    return renderTimeAgo(new Date(context.execution.createdAt));
  },
};

function addPolicyDetails(details: Record<string, string>, policy: VulnerabilityPolicyData): void {
  if (policy.name) details["Name"] = policy.name;
  if (policy.description) details["Description"] = policy.description;
  if (policy.min_severity) details["Minimum Severity"] = policy.min_severity;
  if (policy.package_query_string) details["Package Query"] = policy.package_query_string;
  if (policy.on_violation_quarantine != null) {
    details["Quarantine on Violation"] = policy.on_violation_quarantine ? "Yes" : "No";
  }
  if (policy.allow_unknown_severity != null) {
    details["Allow Unknown Severity"] = policy.allow_unknown_severity ? "Yes" : "No";
  }
}

function buildMetadata(node: NodeInfo): MetadataItem[] {
  const items: MetadataItem[] = [];
  const nodeMetadata = node.metadata as VulnerabilityPolicyNodeMetadata | undefined;
  const configuration = node.configuration as VulnerabilityPolicyConfiguration | undefined;

  if (nodeMetadata?.organizationName) {
    items.push({ icon: "building-2", label: nodeMetadata.organizationName });
  } else if (configuration?.organization) {
    items.push({ icon: "building-2", label: configuration.organization });
  }

  if (nodeMetadata?.policyName) {
    items.push({ icon: "shield", label: nodeMetadata.policyName });
  } else if (configuration?.policy) {
    items.push({ icon: "shield", label: configuration.policy });
  }

  return items;
}

function buildEventSections(nodes: NodeInfo[], execution: ExecutionInfo, componentName: string): EventSection[] {
  if (!execution.rootEvent || !execution.createdAt || !execution.rootEvent.id) {
    return [];
  }

  const rootTriggerNode = nodes.find((n) => n.id === execution.rootEvent?.nodeId);
  const rootTriggerRenderer = getTriggerRenderer(rootTriggerNode?.componentName ?? "");
  const { title } = rootTriggerRenderer.getTitleAndSubtitle({ event: execution.rootEvent });

  return [
    {
      receivedAt: new Date(execution.createdAt),
      eventTitle: title,
      eventSubtitle: renderTimeAgo(new Date(execution.createdAt)),
      eventState: getState(componentName)(execution),
      eventId: execution.rootEvent.id,
    },
  ];
}
